import React, { Component, createRef } from 'react';
import { MapContainer, TileLayer, FeatureGroup, useMap } from 'react-leaflet';
import LocateControl from './LocateControl';
import GeocodingControl from './GeocodingControl';
import CoordinateInputControl from './CoordinateInputControl';
import L from 'leaflet';
// Import Leaflet Draw directly instead of using the React wrapper
import 'leaflet-draw';
import './Map.css';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';

// Fix icon paths
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Create a context for the feature group
const FeatureGroupContext = React.createContext(null);

// This component manages the feature group and draw control
const DrawControl = ({ onChange, onChangeAddress, value, featureGroupRef }) => {
    const map = useMap();
    const drawControlRef = React.useRef(null);
    
    // Set up the draw control with the feature group
    React.useEffect(() => {
        if (!map || !featureGroupRef.current) return;
        
        // Get the Leaflet FeatureGroup instance
        const featureGroup = featureGroupRef.current;
        
        // Clear existing layers and add GeoJSON if provided
        try {
            featureGroup.clearLayers();
            
            if (value) {
                try {
                    const geoJSON = JSON.parse(value);
                    const leafletGeoJSON = new L.GeoJSON(geoJSON);
                    
                    leafletGeoJSON.eachLayer((layer) => {
                        featureGroup.addLayer(layer);
                        if (layer.getBounds && layer.getBounds().isValid()) {
                            map.fitBounds(layer.getBounds());
                        } else if (layer.getLatLng) {
                            map.panTo(layer.getLatLng());
                        }
                    });
                } catch (e) {
                    console.error("Error parsing GeoJSON:", e);
                }
            }
        } catch (e) {
            console.error("Error clearing layers:", e);
        }
        
        // Create the draw control
        try {
            // Set up draw options
            const drawOptions = {
                position: 'topright',
                draw: {
                    polyline: true,
                    polygon: true,
                    circle: true,
                    rectangle: false,
                    marker: true,
                    circlemarker: false
                },
                edit: {
                    featureGroup: featureGroup,
                    remove: true
                }
            };
            
            // Create and add the draw control
            const drawControl = new L.Control.Draw(drawOptions);
            map.addControl(drawControl);
            drawControlRef.current = drawControl;
            
            // Set up event handlers
            map.on(L.Draw.Event.CREATED, function(e) {
                // Clear existing layers before adding new one
                featureGroup.clearLayers();
                
                const layer = e.layer;
                featureGroup.addLayer(layer);
                updateGeometryData(featureGroup);
            });
            
            map.on(L.Draw.Event.EDITED, function(e) {
                updateGeometryData(featureGroup);
            });
            
            map.on(L.Draw.Event.DELETED, function(e) {
                updateGeometryData(featureGroup);
            });
        } catch (e) {
            console.error("Error setting up draw control:", e);
        }
        
        // Clean up function
        return () => {
            try {
                // Clean up event listeners
                map.off(L.Draw.Event.CREATED);
                map.off(L.Draw.Event.EDITED);
                map.off(L.Draw.Event.DELETED);
                
                // Remove the draw control
                if (drawControlRef.current) {
                    map.removeControl(drawControlRef.current);
                    drawControlRef.current = null;
                }
            } catch (e) {
                console.error("Error cleaning up draw control:", e);
            }
        };
    }, [map, featureGroupRef, value]);
    
    // Function to update geometry data
    const updateGeometryData = (featureGroup) => {
        try {
            if (!onChange) return;
            
            const geojsonData = featureGroup.toGeoJSON();
            let geojsonStr = "";
            if (geojsonData && geojsonData.features && geojsonData.features.length > 0) {
                geojsonStr = JSON.stringify(geojsonData.features[0].geometry);
            }
            onChange(geojsonStr);
            
            // Update address if needed
            updateAddress(featureGroup);
        } catch (e) {
            console.error("Error updating geometry data:", e);
        }
    };
    
    // Function to update address
    const updateAddress = (featureGroup) => {
        try {
            if (!onChangeAddress || !map) return;
            
            // Check if point bounds are valid
            if (featureGroup.getBounds && featureGroup.getBounds().isValid()) {
                const center = featureGroup.getBounds().getCenter();
                const geocoder = L.Control.Geocoder.nominatim();
                
                geocoder.reverse(
                    center, 
                    map.options.crs.scale(map.getZoom()), 
                    function (results) {
                        if (results && results.length > 0 && results[0] && results[0].properties) {
                            onChangeAddress(results[0].properties.address || {});
                        } else {
                            onChangeAddress({});
                        }
                    }
                );
            } else {
                onChangeAddress({});
            }
        } catch (e) {
            console.error("Error updating address:", e);
            onChangeAddress({});
        }
    };
    
    return null;
};

export default class Map2 extends Component {
    constructor(props) {
        super(props);
        this.featureGroupRef = createRef();
    }
    
    render() {
        const { disabled = false, onChange, onChangeAddress, value, name } = this.props;
        
        return (
            <div className="map-container" style={{ position: 'relative' }}>
                <FeatureGroupContext.Provider value={this.featureGroupRef}>
                    <MapContainer 
                        className="Map" 
                        id={`map_${name || 'default'}`} 
                        center={[41.885847,12.442812]} 
                        zoom={13} 
                        zoomControl={true}
                        style={{ height: '400px', width: '100%' }}
                        whenCreated={(map) => { setTimeout(() => map.invalidateSize(), 100); }}
                    >
                        <TileLayer
                            attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <FeatureGroup ref={this.featureGroupRef}>
                            <DrawControl 
                                onChange={onChange}
                                onChangeAddress={onChangeAddress}
                                value={value}
                                featureGroupRef={this.featureGroupRef}
                            />
                        </FeatureGroup>
                        <LocateControl />
                        <GeocodingControl />
                        <CoordinateInputControl featureGroupRef={this.featureGroupRef} />
                    </MapContainer>
                </FeatureGroupContext.Provider>
                {disabled && (
                    <div 
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(200, 200, 200, 0.3)',
                            zIndex: 1000,
                            cursor: 'default'
                        }}
                    />
                )}
            </div>
        );
    }
}